/**
 * Bolt QA pane — bulk record tensioning / inspection for mechanical fasteners selected in the model.
 *
 * Flow:
 * 1. User selects IFC mechanical fasteners in the viewer; this pane opens with those GUIDs.
 * 2. We load any matching records from the project DB and split into existing vs new connections.
 * 3. User picks rows in the bottom tables (Bolts in Lion / Bolts not in Lion tabs).
 * 4. Top form shows:
 *    - In-Lion: "Adding QA to X Bolts in Lion" and/or "Overwriting QA on X bolted connection(s)".
 *    - Not in Lion: "Adding new Info and QA to X bolt(s) not in Lion".
 *    - Shared tensioning fields (batch, tensioned/inspected by, date) for all selected rows.
 * 5. Save merges existing + new into one `bolted_connections_attributes` payload for update_multiple.
 */
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import SlidingPane from "modules/5_view_helpers/web/SlidingPane";
import { connect, useSelector } from "react-redux";
import { getProjectBoltedConnectionByGuids, updateMultipleProjectBoltedConnections } from "modules/project/qa/bolted_connections/actions";
import { Button, Card, CardBody, CardHeader, Col, FormGroup, Label, Nav, NavItem, NavLink, Row, TabContent, TabPane } from "reactstrap";
import classnames from "classnames";
import { Field, Form } from "react-final-form";
import { InputField, ReactSelect, required, SingleDateField } from "modules/4_final_form/web";
import _ from "lodash";
import { getUserManagements } from "modules/user_managements/actions";
import { OnChange } from "react-final-form-listeners";
import { PacmanLoader } from "react-spinners";
import NewWindow from "react-new-window";
import moment from "moment";
import TableTorch from "modules/5_view_helpers/web/TableTorch";

const defaultInspectedOn = () => moment().format("YYYY-MM-DD");

// --- TableTorch column configs (bottom selection tables) ---

const EXISTING_COLUMNS = [
  { header: "Main Assembly", dataKey: "main_assembly", sort: true },
  { header: "Phase", dataKey: "phase", sort: true },
  { header: "Lot", dataKey: "lot", sort: true },
  { header: "Qty", dataKey: "qty", sort: true },
  { header: "Size", dataKey: "size", sort: true },
  { header: "Grade", dataKey: "grade", sort: true },
  { header: "Joint Type", dataKey: "joint_type", sort: true },
  { header: "Batch Number", dataKey: "batch_number" },
  { header: "Inspected On", dataKey: "inspected_on", cellRenderer: (cell) => fd(cell) },
  { header: "QA Complete", dataKey: "qa_completed", cellRenderer: (cell) => (cell ? "Yes" : "No") },
];

const NEW_COLUMNS = [
  { header: "Main Assembly", dataKey: "main_assembly", sort: true },
  { header: "Phase", dataKey: "phase", sort: true },
  { header: "Lot", dataKey: "lot", sort: true },
  { header: "Qty", dataKey: "qty", sort: true },
  { header: "Size", dataKey: "size", sort: true },
  { header: "Grade", dataKey: "grade", sort: true },
  { header: "Joint Type", dataKey: "joint_type", sort: true },
  { header: "GUID", dataKey: "guid" },
];

// --- IFC property helpers (used when building new connections from model data) ---

const propertyValue = (properties, groupName, propertyName) =>
  _.get(_.find(_.find(properties, { name: groupName })?.properties, { name: propertyName }), "value", "");

const firstPropertyValue = (properties, groups) => {
  const match = _.find(groups, ([groupName, propertyName]) => {
    const value = propertyValue(properties, groupName, propertyName);
    return value != null && value !== "";
  });
  return match ? propertyValue(properties, match[0], match[1]) : "";
};

// --- Selection / form attribute helpers ---

// TableTorch needs a row id; new connections only have a guid until saved.
const withTableRowId = (connection) => ({
  ...connection,
  id: connection.id || connection.guid,
});

// Get the database ID from a connection object.
const dbId = (connection) => {
  const id = connection?.id;
  if (_.isNil(id) || id === "") return undefined;
  return _.isNumber(id) ? id : parseInt(id, 10) || undefined;
};

// Copy top-level QA field changes into each new connection row in the form.
const applyToAllNewConnections = (form, key, value) => {
  const attributes = form.getState().values.new_bolted_connections_attributes || [];
  form.change(
    "new_bolted_connections_attributes",
    _.map(attributes, (attr) => ({ ...attr, [key]: value }))
  );
};

// Build a new connection hash from IFC/Tekla properties on the selected model object.
const newBoltFromSelectedObject = (selectedObj, inspectedOn) => {
  const properties = selectedObj?.properties;
  const fastener = _.find(properties, { name: "IfcMechanicalFastener" });
  const diameter = _.get(_.find(fastener?.properties, { name: "NominalDiameter" }), "value");
  const length = _.get(_.find(fastener?.properties, { name: "NominalLength" }), "value");
  const material = propertyValue(properties, "IfcMaterial", "Material");
  const qtyValue = firstPropertyValue(properties, [
    ["Default", "QTY"],
    ["Default", "QUANTITY"],
    ["Tekla Bolt", "Number of bolts"],
    ["Tekla Bolt", "Bolt quantity"],
  ]);

  return {
    guid: selectedObj.guid,
    main_assembly: propertyValue(properties, "Tekla Assembly", "Assembly/Cast unit Mark"),
    phase: propertyValue(properties, "Default", "PHASE"),
    lot: propertyValue(properties, "Default", "LOT_NUMBER"),
    qty: qtyValue ? parseInt(qtyValue, 10) || 1 : 1,
    connected_assemblies: [],
    size: `M${diameter}*${length}`,
    grade: _.replace(material, /[a-zA-Z]/g, ""),
    joint_type: _.join(_.get(material.match(/[a-zA-Z]+/g), [], []), ""),
    inspected_on: inspectedOn,
  };
};

// Sync form array when new-tab selection changes; preserve edits for rows still selected.
const buildNewAttributesFromSelection = (newRows, previousAttributes = [], formValues = {}) =>
  _.map(newRows, (row) => {
    const previous = _.find(previousAttributes, (attr) => attr.guid === row.guid);

    if (previous) return { ...previous };

    return {
      ..._.pick(row, [
        "guid",
        "main_assembly",
        "connected_assemblies",
        "phase",
        "lot",
        "qty",
        "size",
        "grade",
        "joint_type",
        "inspected_on",
      ]),
      connected_assemblies: row.connected_assemblies || [],
      batch_number: formValues.batch_number || "",
      tensioned_by_id: formValues.tensioned_by_id ?? null,
      inspected_by_id: formValues.inspected_by_id ?? null,
      inspected_on: formValues.inspected_on || row.inspected_on || defaultInspectedOn(),
    };
  });

// Existing rows only need id/guid plus QA fields — no schema editing in the UI.
const buildExistingAttributesForSave = (existingRows, formValues) =>
  _.map(existingRows, (row) => ({
    id: dbId(row),
    guid: row.guid,
    batch_number: formValues.batch_number || row.batch_number || "",
    tensioned_by_id: formValues.tensioned_by_id ?? row.tensioned_by_id,
    inspected_by_id: formValues.inspected_by_id ?? row.inspected_by_id,
    inspected_on: formValues.inspected_on || row.inspected_on || defaultInspectedOn(),
  }));

const qaFieldsFromValues = (values) => ({
  batch_number: values.batch_number || "",
  tensioned_by_id: values.tensioned_by_id ?? null,
  inspected_by_id: values.inspected_by_id ?? null,
  inspected_on: values.inspected_on || defaultInspectedOn(),
});

const existingConnectionLabel = (connection) =>
  _.join(_.compact([connection.main_assembly, connection.size, connection.phase, connection.lot]), " · ") ||
  connection.guid;

const mergeAttributesIntoRows = (rows, attributes) => {
  const attrByGuid = _.keyBy(attributes, "guid");
  return _.map(rows, (row) => withTableRowId({ ...row, ...attrByGuid[row.guid] }));
};

// Resolve a guid list back to full row objects from a table dataset.
// Selection state only stores guids; TableTorch and form builders need the matching rows.
// GUID comparison is case-insensitive because IFC/model and DB values may differ in casing.
const rowsForGuids = (tableData, guids) => {
  const guidSet = _.map(guids, _.toLower);
  return _.filter(tableData, (row) => _.includes(guidSet, _.toLower(row.guid)));
};

const BoltQaPane = (props) => {
  // --- State ---
  const { isOpen, toggle, project_id, selectedObjects, showInNewWindow } = props;
  const OpenInType = showInNewWindow ? NewWindow : SlidingPane;

  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("existing");
  const [selectedGuids, setSelectedGuids] = useState({ existing: [], new: [] }); // checked rows per bottom tab
  const [newConnectionAttributes, setNewConnectionAttributes] = useState([]); // form mirror for new-row render
  const formRef = useRef(null);

  const project_qa_bolted_connections = useSelector((state) => state.project_qa_bolted_connections);
  const users = useSelector((state) => state.users);
  const filtered_bolts = _.filter(selectedObjects, { class: "IFCMECHANICALFASTENER" });

  // Load DB records for selected GUIDs and user list for tensioning dropdowns.
  useEffect(() => {
    const guids = _.map(filtered_bolts, "guid");
    if (_.isNotEmpty(guids)) {
      props.getProjectBoltedConnectionByGuids(project_id, guids);
    }
    props.getUserManagements();
  }, []);

  // Split model selection: in DB → existing tab; not in DB → new tab (IFC-derived fields).
  const { existingBoltedConnections, newBoltedConnections } = useMemo(() => {
    const inspectedOn = defaultInspectedOn();
    const connectionsByGuid = _.keyBy(project_qa_bolted_connections, (c) => _.toLower(c?.guid));

    const groups = _.reduce(
      filtered_bolts,
      (acc, selectedObj) => {
        const guid = selectedObj?.guid;
        if (_.isEmpty(guid)) return acc;

        const existingConnection = connectionsByGuid[_.toLower(guid)];
        if (existingConnection) {
          acc.existingBoltedConnections.push({
            ...existingConnection,
            inspected_on: existingConnection.inspected_on || inspectedOn,
          });
        } else {
          acc.newBoltedConnections.push(newBoltFromSelectedObject(selectedObj, inspectedOn));
        }
        return acc;
      },
      { existingBoltedConnections: [], newBoltedConnections: [] }
    );

    const sortByAssembly = (connections) => _.orderBy(connections, [(c) => _.toLower(c.main_assembly || "")]);

    return {
      existingBoltedConnections: sortByAssembly(groups.existingBoltedConnections),
      newBoltedConnections: sortByAssembly(groups.newBoltedConnections),
    };
  }, [filtered_bolts, project_qa_bolted_connections]);

  const existingTableData = useMemo(() => _.map(existingBoltedConnections, withTableRowId), [existingBoltedConnections]);
  const newTableData = useMemo(() => _.map(newBoltedConnections, withTableRowId), [newBoltedConnections]);

  // Table selection → update guid lists and rebuild the editable new-connections form array.
  const handleSelectionChange = useCallback(
    (tab, guids) => {
      const nextSelection = {
        existing: tab === "existing" ? guids : selectedGuids.existing,
        new: tab === "new" ? guids : selectedGuids.new,
      };

      setSelectedGuids(nextSelection);

      const formValues = formRef.current?.getState().values || {};
      const newAttributes = buildNewAttributesFromSelection(
        rowsForGuids(newBoltedConnections, nextSelection.new),
        formValues.new_bolted_connections_attributes,
        formValues
      );

      setNewConnectionAttributes(newAttributes);

      // Defer form.change so React state and final-form stay in sync after selection.
      queueMicrotask(() => {
        formRef.current?.change("new_bolted_connections_attributes", newAttributes);
      });
    },
    [selectedGuids, newBoltedConnections]
  );

  const selectedExisting = useMemo(
    () => rowsForGuids(existingBoltedConnections, selectedGuids.existing),
    [existingBoltedConnections, selectedGuids.existing]
  );

  const selectedCount = _.size(selectedGuids.existing) + _.size(selectedGuids.new);

  // Merge read-only existing QA updates + editable new rows into one save payload.
  const handleSave = (values) => {
    setSaving(true);
    const existingAttributes = buildExistingAttributesForSave(selectedExisting, values);
    const newAttributes = _.map(values.new_bolted_connections_attributes, (attr) => ({
      ...attr,
      ...qaFieldsFromValues(values),
    }));

    props
      .updateMultipleProjectBoltedConnections(project_id, {
        bolted_connections_attributes: _.concat(existingAttributes, newAttributes),
      })
      .then(() => {
        toggle();
        toastr.success("Bolt QA Saved");
        setSaving(false);
      })
      .catch(() => {
        setSaving(false);
        toastr.error("Error saving bolt QA");
      });
  };

  return (
    <OpenInType isOpen={isOpen} onRequestClose={toggle} onUnload={toggle} width="95%" title="Bolt QA">
      {saving && <PacmanLoader />}

      {!saving && (
        <Form
          onSubmit={handleSave}
          subscription={{ values: true }}
          initialValues={{
            batch_number: "",
            tensioned_by_id: null,
            inspected_by_id: null,
            inspected_on: defaultInspectedOn(),
            new_bolted_connections_attributes: [],
          }}
          render={({ handleSubmit, form, values }) => {
            formRef.current = form;

            // Prefer form values; fall back to React state while selection sync is in flight.
            const newAttributes = _.isNotEmpty(values.new_bolted_connections_attributes)
              ? values.new_bolted_connections_attributes
              : newConnectionAttributes;
            const displayNewTableData = mergeAttributesIntoRows(newTableData, newAttributes);
            const newConnections = _.map(newAttributes, (connection, index) => ({ connection, index }));

            return (
              <Fragment>
                {/* Top: selection summary, new-row editor, shared QA fields, save */}
                <Card className="mb-4">
                  <CardHeader>Bolt QA Details</CardHeader>
                  <CardBody>
                    <BoltedConnectionsAttributes
                      form={form}
                      users={users}
                      selectedCount={selectedCount}
                      selectedExisting={selectedExisting}
                      newConnections={newConnections}
                    />

                    <Row className="mt-3">
                      <Col>
                        <Button color="success" onClick={handleSubmit} disabled={selectedCount === 0}>
                          Save Bolt QA
                        </Button>
                      </Col>
                      <Col className="text-right">
                        <Button color="secondary" outline onClick={toggle}>
                          Close
                        </Button>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {/* Bottom: browse and select existing / new connections */}
                <Card>
                  <CardHeader>Select Connections</CardHeader>
                  <CardBody>
                    <Nav tabs>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === "existing" })}
                          onClick={() => setActiveTab("existing")}
                          style={{ cursor: "pointer" }}
                        >
                          Bolts in Lion ({_.size(existingBoltedConnections)})
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === "new" })}
                          onClick={() => setActiveTab("new")}
                          style={{ cursor: "pointer" }}
                        >
                          Bolts not in Lion ({_.size(newBoltedConnections)})
                        </NavLink>
                      </NavItem>
                    </Nav>

                    <TabContent activeTab={activeTab} className="pt-3">
                      <TabPane tabId="existing">
                        {activeTab === "existing" && (
                          <TableTorch
                            tableId="bolt-qa-existing"
                            data={existingTableData}
                            columns={EXISTING_COLUMNS}
                            selectable
                            selectedRowsOverride={rowsForGuids(existingTableData, selectedGuids.existing)}
                            setSelectedRowsOverride={(rows) => handleSelectionChange("existing", _.map(rows, "guid"))}
                            rowStyle={(row) => (row.qa_completed ? { backgroundColor: "#fff3cd" } : {})}
                          />
                        )}
                      </TabPane>
                      <TabPane tabId="new">
                        {activeTab === "new" && (
                          <TableTorch
                            tableId="bolt-qa-new"
                            data={displayNewTableData}
                            columns={NEW_COLUMNS}
                            selectable
                            selectedRowsOverride={rowsForGuids(displayNewTableData, selectedGuids.new)}
                            setSelectedRowsOverride={(rows) => handleSelectionChange("new", _.map(rows, "guid"))}
                          />
                        )}
                      </TabPane>
                    </TabContent>
                  </CardBody>
                </Card>
              </Fragment>
            );
          }}
        />
      )}
    </OpenInType>
  );
};

export default connect(null, { getProjectBoltedConnectionByGuids, getUserManagements, updateMultipleProjectBoltedConnections })(BoltQaPane);

// --- Top-form subcomponents ---

const cellInput = { width: "100%", padding: "1px 2px", margin: 0, boxSizing: "border-box" };

const CellInput = ({ input, meta: { touched, error, submitFailed } }) => {
  const showError = error && (touched || submitFailed);

  return (
    <Fragment>
      <input
        {...input}
        style={{
          ...cellInput,
          border: showError ? "1px solid #dc3545" : "1px solid #ccc",
        }}
      />
      {showError && (
        <div style={{ color: "#dc3545", fontSize: "11px", lineHeight: 1.2, marginTop: "1px" }}>{error}</div>
      )}
    </Fragment>
  );
};

// Editable per-row schema fields for new connections only.
const NewConnectionsTable = ({ newConnections }) => (
  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", marginBottom: "8px" }}>
    <thead>
      <tr>
        {_.map(["Assembly", "Phase", "Lot", "Qty", "Size", "Grade", "Joint"], (label) => (
          <th key={label} style={{ padding: "2px 4px", borderBottom: "1px solid #ccc", textAlign: "left" }}>
            {label}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {_.map(newConnections, ({ connection, index }) => {
        const field = `new_bolted_connections_attributes[${index}]`;

        return (
          <tr key={connection.guid || index}>
            <td style={{ padding: "1px 2px" }}>
              <Field name={`${field}.main_assembly`} component={CellInput} validate={required} />
            </td>
            <td style={{ padding: "1px 2px" }}>
              <Field name={`${field}.phase`} component={CellInput} validate={required} />
            </td>
            <td style={{ padding: "1px 2px" }}>
              <Field name={`${field}.lot`} component={CellInput} validate={required} />
            </td>
            <td style={{ padding: "1px 2px", width: "40px" }}>
              <Field name={`${field}.qty`} component={CellInput} validate={required} />
            </td>
            <td style={{ padding: "1px 2px" }}>
              <Field name={`${field}.size`} component={CellInput} validate={required} />
            </td>
            <td style={{ padding: "1px 2px" }}>
              <Field name={`${field}.grade`} component={CellInput} validate={required} />
            </td>
            <td style={{ padding: "1px 2px" }}>
              <Field name={`${field}.joint_type`} component={CellInput} validate={required} />
              <Field name={`${field}.guid`} component="input" type="hidden" />
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

// Selected in-Lion bolts: adding QA vs overwriting completed QA (list overwrites only).
const InLionSelectedSummary = ({ connections }) => {
  if (_.isEmpty(connections)) return null;

  const overwriting = _.filter(connections, "qa_completed");
  const adding = _.reject(connections, "qa_completed");

  return (
    <div className="mb-2">
      {_.isNotEmpty(adding) && (
        <div>Adding QA to {_.size(adding)} Bolt{_.size(adding) === 1 ? "" : "s"} in Lion.</div>
      )}
      {_.isNotEmpty(overwriting) && (
        <Fragment>
          <div className={classnames({ "mt-1": _.isNotEmpty(adding) })}>
            Overwriting QA on {_.size(overwriting)} bolted connection{_.size(overwriting) === 1 ? "" : "s"}.
          </div>
          <ul style={{ margin: "4px 0 0", paddingLeft: "18px" }}>
            {_.map(overwriting, (connection) => (
              <li key={connection.guid}>{existingConnectionLabel(connection)}</li>
            ))}
          </ul>
        </Fragment>
      )}
    </div>
  );
};

// Top form body: summaries + new table + tensioning fields shared across all selections.
const BoltedConnectionsAttributes = ({ form, users, selectedCount, selectedExisting, newConnections }) => {
  const userSelectOptions = _.map(users, (u) => ({ value: u.id, label: u.name }));

  return (
    <Fragment>
      <InLionSelectedSummary connections={selectedExisting} />

      {_.isNotEmpty(newConnections) && (
        <Fragment>
          <div className="mb-1">
            Adding new Info and QA to {_.size(newConnections)} bolt{_.size(newConnections) === 1 ? "" : "s"} not in
            Lion.
          </div>
          <NewConnectionsTable newConnections={newConnections} />
          <hr />
        </Fragment>
      )}

      <h6 className="text-uppercase text-muted mb-3">Bolt Tensioning Details</h6>
      <p className="text-muted small">Applies to all selected connections.</p>

      <Row>
        <Col md={6}>
          <FormGroup>
            <Label>Batch Number</Label>
            <Field name="batch_number" component={InputField} validate={required} />
            <OnChange name="batch_number">
              {(value) => applyToAllNewConnections(form, "batch_number", value)}
            </OnChange>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label>Tensioned By</Label>
            <Field name="tensioned_by_id" component={ReactSelect} validate={required} options={userSelectOptions} />
            <OnChange name="tensioned_by_id">
              {(value) => applyToAllNewConnections(form, "tensioned_by_id", value)}
            </OnChange>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <FormGroup>
            <Label>Inspected By</Label>
            <Field name="inspected_by_id" component={ReactSelect} validate={required} options={userSelectOptions} />
            <OnChange name="inspected_by_id">
              {(value) => applyToAllNewConnections(form, "inspected_by_id", value)}
            </OnChange>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label>Inspected On</Label>
            <Field
              name="inspected_on"
              component={SingleDateField}
              validate={required}
              enablePreviousDates
              initialValue={defaultInspectedOn()}
            />
            <OnChange name="inspected_on">
              {(value) => applyToAllNewConnections(form, "inspected_on", value)}
            </OnChange>
          </FormGroup>
        </Col>
      </Row>
    </Fragment>
  );
};
